import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { operadoresApi, type Operador, type OperadorInput } from '../api/operadores'
import { Table, type Column } from '../components/ui/Table'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { Input } from '../components/ui/Input'
import { GenericBadge } from '../components/ui/Badge'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

const operadorSchema = z.object({
  nombre: z.string().min(2, 'Mínimo 2 caracteres'),
  documentoCi: z.string().optional(),
  telefono: z.string().optional(),
  pinAcceso: z.string().length(6, 'PIN debe ser de 6 dígitos').regex(/^\d+$/, 'Solo dígitos'),
})

type OperadorForm = z.infer<typeof operadorSchema>

export default function Operadores() {
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Operador | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['operadores'],
    queryFn: () => operadoresApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: (d: OperadorInput) => operadoresApi.create(d),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['operadores'] }); setModalOpen(false) },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) => operadoresApi.toggleActivo(id, activo),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['operadores'] }),
  })

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<OperadorForm>({
    resolver: zodResolver(operadorSchema),
  })

  function openCreate() {
    setEditTarget(null)
    reset({})
    setModalOpen(true)
  }

  async function onSubmit(values: OperadorForm) {
    await createMutation.mutateAsync(values)
    reset()
  }

  const COLUMNS: Column<Operador>[] = [
    { key: 'nombre', header: 'Nombre', sortable: true, render: (r) => r.nombre },
    { key: 'documentoCi', header: 'CI', render: (r) => r.documentoCi ?? '—' },
    { key: 'telefono', header: 'Teléfono', render: (r) => r.telefono ?? '—' },
    { key: 'activo', header: 'Estado', render: (r) => <GenericBadge label={r.activo ? 'Activo' : 'Inactivo'} variant={r.activo ? 'success' : 'default'} /> },
    {
      key: 'acciones',
      header: '',
      render: (r) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); toggleMutation.mutate({ id: r.id, activo: !r.activo }) }}
            className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            {r.activo ? 'Desactivar' : 'Activar'}
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Operadores</h1>
        <Button size="sm" onClick={openCreate}>+ Nuevo operador</Button>
      </div>

      <Table
        columns={COLUMNS}
        data={data?.items ?? []}
        keyExtractor={(r) => r.id}
        loading={isLoading}
        emptyMessage="Sin operadores registrados"
      />

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? 'Editar operador' : 'Nuevo operador'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input {...register('nombre')} label="Nombre completo" error={errors.nombre?.message} />
          <Input {...register('documentoCi')} label="Cédula de identidad" placeholder="opcional" />
          <Input {...register('telefono')} label="Teléfono" placeholder="opcional" />
          <Input
            {...register('pinAcceso')}
            label="PIN de acceso (6 dígitos)"
            type="password"
            maxLength={6}
            placeholder="••••••"
            error={errors.pinAcceso?.message}
          />
          {createMutation.isError && (
            <p className="text-xs text-danger">Error al guardar. Verifique los datos.</p>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={isSubmitting}>Guardar</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
